import React from 'react';

/**
 * DetectiveDino - The mascot dino holding a magnifying glass
 * Colors: Aquamarine dino, PURPLE magnifying glass, PURPLE thought bubble
 * Used for:
 * - Error pages ("Uh oh! I'm having trouble loading this page!")
 * - Library tab icon
 * - App icon representation
 */
export const DetectiveDino = ({ size = 120, className = '', animated = false, showMagnifyingGlass = true }) => {
  // CORRECT Colors - PURPLE magnifying glass and thought bubble
  const DINO_COLOR = '#00E5CC';           // Aquamarine dino
  const PURPLE_COLOR = '#8B5CF6';         // Purple for magnifying glass AND thought bubble
  const LENS_FILL = 'rgba(139, 92, 246, 0.2)'; // Light purple lens
  const BACKGROUND_COLOR = '#1a1a2e';     // Dark background
  
  return (
    <div 
      className={`inline-block ${className} ${animated ? 'animate-bounce' : ''}`}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ imageRendering: 'pixelated' }}
      >
        {/* Background - rounded rectangle */}
        <rect fill={BACKGROUND_COLOR} width="64" height="64" rx="12"/>
        
        {/* Dino Body - Pixel Art */}
        <g transform="translate(4,8)">
          {/* Head top */}
          <rect fill={DINO_COLOR} x="18" y="0" width="20" height="5"/>
          {/* Head middle with eye space */}
          <rect fill={DINO_COLOR} x="15" y="5" width="26" height="5"/>
          {/* Eye - white */}
          <rect fill="#FFFFFF" x="21" y="5" width="5" height="5"/>
          {/* Head bottom */}
          <rect fill={DINO_COLOR} x="15" y="10" width="26" height="5"/>
          {/* Neck */}
          <rect fill={DINO_COLOR} x="15" y="15" width="15" height="5"/>
          {/* Body upper - extends toward magnifying glass */}
          <rect fill={DINO_COLOR} x="10" y="20" width="22" height="5"/>
          {/* Body middle */}
          <rect fill={DINO_COLOR} x="3" y="25" width="25" height="5"/>
          {/* Body lower */}
          <rect fill={DINO_COLOR} x="0" y="30" width="22" height="5"/>
          {/* Tail/feet */}
          <rect fill={DINO_COLOR} x="3" y="35" width="17" height="5"/>
        </g>
        
        {showMagnifyingGlass && (
          <>
            {/* PURPLE Thought Bubble with Question Mark */}
            <circle cx="50" cy="6" r="8" fill={PURPLE_COLOR}/>
            <text 
              x="50" 
              y="10" 
              textAnchor="middle" 
              fontSize="12" 
              fontWeight="bold" 
              fill={BACKGROUND_COLOR}
            >?</text>
            
            {/* Small dots connecting bubble to dino */}
            <circle cx="42" cy="12" r="1.5" fill={PURPLE_COLOR}/>
            <circle cx="38" cy="15" r="1" fill={PURPLE_COLOR}/>
            
            {/* PURPLE Magnifying Glass */}
            <circle 
              cx="50" 
              cy="22" 
              r="10" 
              fill="none" 
              stroke={PURPLE_COLOR} 
              strokeWidth="3"
            />
            
            {/* Handle */}
            <rect 
              x="56" 
              y="30" 
              width="6" 
              height="3" 
              rx="1" 
              fill={PURPLE_COLOR} 
              transform="rotate(45 56 30)"
            />
            
            {/* Lens - subtle purple fill */}
            <circle 
              cx="50" 
              cy="22" 
              r="7" 
              fill={LENS_FILL}
            />
          </>
        )}
      </svg>
    </div>
  );
};

/**
 * LibraryDino - The mascot dino with magnifying glass for the Library tab
 * Smaller version optimized for navigation icons - no background
 */
export const LibraryDino = ({ size = 24, className = '' }) => {
  const DINO_COLOR = '#00E5CC';
  const PURPLE_COLOR = '#8B5CF6';
  const LENS_FILL = 'rgba(139, 92, 246, 0.2)';
  
  return (
    <div className={`inline-block ${className}`} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 56 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ imageRendering: 'pixelated' }}
      >
        {/* PURPLE Thought Bubble */}
        <circle cx="40" cy="4" r="5" fill={PURPLE_COLOR}/>
        <text x="40" y="7" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#1a1a2e">?</text>
        <circle cx="34" cy="8" r="1" fill={PURPLE_COLOR}/>
        
        {/* Dino Body */}
        <g transform="translate(0,6)">
          <rect fill={DINO_COLOR} x="14" y="0" width="16" height="4"/>
          <rect fill={DINO_COLOR} x="12" y="4" width="20" height="4"/>
          <rect fill="#FFFFFF" x="17" y="4" width="4" height="4"/>
          <rect fill={DINO_COLOR} x="12" y="8" width="20" height="4"/>
          <rect fill={DINO_COLOR} x="12" y="12" width="12" height="4"/>
          <rect fill={DINO_COLOR} x="8" y="16" width="18" height="4"/>
          <rect fill={DINO_COLOR} x="2" y="20" width="20" height="4"/>
          <rect fill={DINO_COLOR} x="0" y="24" width="18" height="4"/>
          <rect fill={DINO_COLOR} x="2" y="28" width="14" height="4"/>
        </g>
        
        {/* PURPLE Magnifying Glass */}
        <circle cx="44" cy="18" r="8" stroke={PURPLE_COLOR} strokeWidth="2.5" fill="none"/>
        <rect x="49" y="24" width="5" height="2.5" rx="1" fill={PURPLE_COLOR} transform="rotate(45 49 24)"/>
        <circle cx="44" cy="18" r="5.5" fill={LENS_FILL}/>
      </svg>
    </div>
  );
};

/**
 * ErrorDino - EXACT copy of header DinoIcon, just scaled up.
 * Dino faces right, thought bubble on LEFT, arm holds magnifying glass on RIGHT.
 */
export const ErrorDino = ({ size = 160, className = '', animated = false }) => {
  const pixels = [
    [0,0,0,0,0,0,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,2,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,0,0,0],
    [0,0,0,1,0,1,1,1,1,1,0,0],
    [0,0,1,1,1,1,1,1,1,1,0,0],
    [0,1,1,1,1,1,1,1,1,0,0,0],
    [1,1,1,1,1,1,1,1,0,0,0,0],
    [0,1,1,1,1,1,1,1,0,0,0,0],
    [0,0,1,1,1,1,1,0,0,0,0,0],
    [0,0,0,1,1,0,1,1,0,0,0,0],
    [0,0,0,1,0,0,0,1,0,0,0,0],
    [0,0,0,1,1,0,0,0,0,0,0,0],
  ];
  const fills = { 1: '#00E5CC', 2: '#FFFFFF' };
  
  return (
    <div className={`inline-block ${className} ${animated ? 'animate-bounce' : ''}`} style={{ width: size, height: size * 0.77 }}>
      <svg
        width={size}
        height={size * 0.77}
        viewBox="-2 4 64 52"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ imageRendering: 'pixelated' }}
      >
        {pixels.map((row, y) => 
          row.map((pixel, x) => 
            pixel > 0 && (
              <rect
                key={`${x}-${y}`}
                x={x * 4}
                y={y * 4}
                width="4"
                height="4"
                fill={fills[pixel]}
              />
            )
          )
        )}
        
        {/* Arm holding the magnifying glass — same as header */}
        <line x1="40" y1="28" x2="46" y2="32" stroke="#00E5CC" strokeWidth="3" strokeLinecap="round"/>
        
        {/* Magnifying glass — right side, same as header */}
        <circle cx="52" cy="26" r="5" fill="none" stroke="#7C3AED" strokeWidth="2"/>
        <circle cx="52" cy="26" r="3" fill="#7C3AED" fillOpacity="0.15"/>
        <line x1="48" y1="30" x2="44" y2="34" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"/>
        
        {/* Thought bubble with ? — LEFT side, same as header */}
        <circle cx="6" cy="10" r="4.5" fill="#7C3AED"/>
        <circle cx="13" cy="11" r="1.5" fill="#7C3AED"/>
        <circle cx="17" cy="13" r="1" fill="#7C3AED"/>
        <text x="6" y="12.5" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">?</text>
      </svg>
    </div>
  );
};

/**
 * ErrorPageContent - Shows Detective Dino with an error message
 */
export const ErrorPageContent = ({ 
  isSpanish = false, 
  onRetry,
  title,
  message 
}) => {
  const defaultTitle = isSpanish 
    ? '¡Uh oh!' 
    : 'Uh oh!';
  
  const defaultMessage = isSpanish
    ? '¡Estoy teniendo problemas para cargar esta página!'
    : "I'm having trouble loading this page!";

  return (
    <div className="max-w-md mx-auto text-center">
      <ErrorDino size={160} className="mx-auto mb-6" />
      
      <h2 className="text-2xl font-bold text-foreground mb-3">
        {title || defaultTitle}
      </h2>
      
      <p className="text-muted-foreground mb-6">
        {message || defaultMessage}
      </p>
      
      <div className="space-y-3">
        <button 
          onClick={onRetry || (() => window.location.reload())}
          className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-[#0d1f2d] bg-[#00E5CC] hover:bg-[#00C9B2] rounded-lg transition-colors w-full"
        >
          {isSpanish ? '¿Intentar de nuevo?' : 'Try again?'}
        </button>
        
        <p className="text-xs text-muted-foreground">
          {isSpanish 
            ? 'Si el problema persiste, por favor contacta soporte.'
            : 'If the problem persists, please contact support.'}
        </p>
      </div>
    </div>
  );
};

export default DetectiveDino;
